import React, { useReducer } from 'react';
import {
    convertStartToCSSOffset,
    convertToCSSWidth,
    normalizeClassName,
    NOTE_HEIGHT,
    NOTE_WIDTH,
    positionToTick,
    tickToPosition,
} from '../../render/renderUtils';
import {
    Note,
    NoteDuration,
    NoteVelocity,
    Tone
} from '../../model/core';
import {
    ContainerAtom,
    HorizontalNoteContainer,
    NoteRangeType
} from '../../model/containers';
import { NoteSelectionGroup } from '../../utils/NoteSelectionGroup';

import './NoteGlyph.css';
import { isBlackKey } from './PianoKeybed';
import {
    useNoteSelectionGroups,
    useSetNoteSelectionGroups
} from '../../stores/InspectorPanelStore';
import { useZoomStore } from '../../stores/ZoomStore';
import { workspaceStore } from '../../stores/WorkspaceStore';
import { TrackColor } from '../../render/trackColor';

export function NoteGlyph({
    trackId,
    start,
    note,
    duration,
    velocity = 100,
    noteRange,
    topOffset,
    fixedDuration,
    updateNoteDurationCB,
    moveNote,
    removeNoteCB,
    extraClasses,
    updateHNC,
    trackColor = TrackColor.DEFAULT_COLOR,
    normalizeDropPosition = (x: number, zoomFactor: number) => x,
}: {
    trackId: number,
    start: number,
    note: ContainerAtom<Note>,
    duration: NoteDuration,
    velocity?: NoteVelocity,
    noteRange: NoteRangeType,
    topOffset?: number,
    fixedDuration?: boolean,
    updateNoteDurationCB: (delta: number) => void
    moveNote: (
        start: number,
        note: ContainerAtom<Note>, // FIXME: this pretty odd in the case of chords.
        deltaX: number,
        deltaY: number,
    ) => void,
    removeNoteCB: (start: number, note: ContainerAtom<Note | Tone>) => void,
    extraClasses?: string[],
    updateHNC?: (hnc: HorizontalNoteContainer) => void,
    trackColor?: TrackColor.Color,
    normalizeDropPosition?: (x: number, zoomFactor: number) => number,
}) {

    const [, forceUpdate] = useReducer(x => x + 1, 0);
    const setWorkspaceSelectionGroups = useSetNoteSelectionGroups();
    const zoomFactor = useZoomStore().zoomFactor;
    const noteSelectionGroups = useNoteSelectionGroups();
    const setNoteSelectionGroups = useSetNoteSelectionGroups();

    const width = convertToCSSWidth(duration, zoomFactor);
    const noteSelectionGroup = noteSelectionGroups.get(trackId);

    const style = {
        width: width,
        left: convertStartToCSSOffset(start, zoomFactor),
        top: `calc((${107} - ${note.payload.index} - ${107 - noteRange.max} - 1) * var(--note-box-height) + ${topOffset ? topOffset : 0}px)`,
        height: 'var(--note-box-height)',
        borderRadius: '1px',
    };
    const classes = ["piano-roll-note", "piano-roll-note-active"];
    if (extraClasses) classes.push(...extraClasses);
    if (noteSelectionGroup?.contains(start, note.payload)) classes.push("group-selection--included");

    classes.push(isBlackKey(note.payload) ? "piano-roll--black-key" : "piano-roll--white-key");

    let resizeX = 0;
    let startX = 0;
    let startY = 0;
    let startTick = 0;

    var isMoving  = false;

    return (<>
        <div className={normalizeClassName(classes)} style={style} >
            <div style={
                {
                    border: '0px',
                    margin: '0px',
                    height: '100%',
                    width: width,
                    backgroundColor: trackColor,
                    borderRadius: '1px',
                }}
                onClick={(e) => handleGlyphClick(
                    e,
                    start,
                    note,
                    duration,
                    removeNoteCB,
                    (g: NoteSelectionGroup) => {
                        noteSelectionGroups.set(trackId, g);
                        setNoteSelectionGroups(new Map(noteSelectionGroups));
                    },
                    (g: NoteSelectionGroup) => {
                        noteSelectionGroups.set(trackId, g);
                        setWorkspaceSelectionGroups(new Map(noteSelectionGroups));
                    },
                    forceUpdate,
                    noteSelectionGroup
                )}
                onDragStart={(e) => {
                    startX = e.clientX;
                    startY = e.clientY;
                    startTick = positionToTick(NOTE_WIDTH, zoomFactor, startX);
                    e.currentTarget.style.opacity = '0.4';
                    isMoving = true;
                }}
                onDragEnd={(e) => {
                    if (!noteSelectionGroup || noteSelectionGroup.getNotes()?.length < 1) return;
                    const diffX = e.clientX - startX;
                    const diffY = 0 | (e.clientY - startY) / NOTE_HEIGHT;
                    const tickDelta = positionToTick(NOTE_WIDTH, zoomFactor, diffX);
                    const referenceStart = noteSelectionGroup.getNotes()[0].step + tickDelta;
                    const normalizedPosition = normalizeDropPosition(referenceStart, zoomFactor);
                    const referenceShift = normalizedPosition - noteSelectionGroup.getNotes()[0].step;
                    if (noteSelectionGroup) {
                        noteSelectionGroup.move(
                            (start: number) => referenceShift + start,
                            (note: number) => note - diffY,
                            updateHNC
                        );
                        // move the overlay too
                        const newNoteSelectionGroup = new NoteSelectionGroup(
                            noteSelectionGroup.getContainer(),
                            {
                                start: noteSelectionGroup.getGeometry().start + referenceShift,
                                top: noteSelectionGroup.getGeometry().top - diffY,
                                left: noteSelectionGroup.getGeometry().start + referenceShift,
                                width: noteSelectionGroup.getGeometry().width,
                                height: noteSelectionGroup.getGeometry().height
                            },
                            noteSelectionGroup.getNotes()
                        );
                        noteSelectionGroups.set(trackId, newNoteSelectionGroup);
                        setNoteSelectionGroups(new Map(noteSelectionGroups));
                    } else {
                        console.log('is this ever reached?');
                        moveNote(start, note, diffX, diffY);
                    }
                    forceUpdate();
                    isMoving = false;
                }}

                onDrag={(e) => {
                    if (isMoving) {
                        const diffX = e.clientX - startX;
                        const diffY = 0 | (e.clientY - startY) / NOTE_HEIGHT;
                        const tickDelta = positionToTick(NOTE_WIDTH, zoomFactor, diffX);
                        const referenceShift = tickToPosition(
                            NOTE_WIDTH,
                            zoomFactor,
                            normalizeDropPosition(start + tickDelta, zoomFactor) - start
                        );

                        e.currentTarget.style.cursor = 'grabbing';
                        if (e.currentTarget.parentElement) {
                            e.currentTarget.parentElement.style.boxShadow = `${referenceShift}px ${diffY * NOTE_HEIGHT}px 0px 0px rgb(0, 255, 85)`;
                        }
                    } else {
                        e.currentTarget.style.cursor = 'grab';
                        if (e.currentTarget.parentElement) {
                            e.currentTarget.parentElement.style.boxShadow = 'none';
                        }
                    }
                }}

                title={`${duration.value} ${note.payload.getName()} @ ${start}`}
            />
            {!fixedDuration && <div className="piano-roll--note-duration-handle"
                draggable="true"
                onDragStart={(e: { clientX: number, dataTransfer: any }) => resizeX = e.clientX}
                onDragEnd={(e) => {
                    noteSelectionGroup?.clear();
                    const diff = e.clientX - resizeX;
                    const ticks = 0 | (diff * zoomFactor / NOTE_WIDTH);
                    updateNoteDurationCB(ticks);
                }}
                onDrag={(e) => {
                    const diff = e.clientX - resizeX;
                    const tickDelta = positionToTick(NOTE_WIDTH, zoomFactor, diff);
                    const referenceShift = tickToPosition(
                        NOTE_WIDTH,
                        zoomFactor,
                        normalizeDropPosition(start + tickDelta, zoomFactor) - start
                    );

                    // "shadow" indicating the stretching change.
                    if (e.currentTarget.parentElement) {
                        e.currentTarget.parentElement.style.width = `calc(${width} + ${referenceShift}px)`;
                    }
                }}

            />}
        </div>
    </>);
}

function handleGlyphClick(
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    start: number,
    note: ContainerAtom<Note>,
    duration: NoteDuration,
    removeNoteCB: (start: number, note: ContainerAtom<Note | Tone>) => void,
    setNoteSelectionGroup?: (group: NoteSelectionGroup | undefined) => void,
    setWorkspaceSelectionGroup?: (group: NoteSelectionGroup | undefined) => void,
    forceUpdate?: () => void,
    noteSelectionGroup?: NoteSelectionGroup,
) {
    if (e.ctrlKey) {
        if (noteSelectionGroup?.contains(start, note.payload)) {
            noteSelectionGroup?.removeNote(start, note.payload);
        } else {
            noteSelectionGroup?.addNote(start, note.payload, duration, 100);
        }
        setNoteSelectionGroup?.(noteSelectionGroup);
        setWorkspaceSelectionGroup?.(noteSelectionGroup);
    } else {
        if (noteSelectionGroup) {
            setNoteSelectionGroup?.(undefined);
            setWorkspaceSelectionGroup?.(undefined);
        } else {
            if (workspaceStore.getState().uiState.editMode === 'NOTES') {
                removeNoteCB(start, note);
            }
        }

    }
    if (forceUpdate) forceUpdate();
}